// components/ui/NSPDatepicker.tsx
import React from 'react';
import { Button, DatePicker } from 'antd';
import { LeftOutlined, RightOutlined, CalendarOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import styles from './Button.module.css';

interface NSPDatePickerProps {
    value?: Dayjs | null;
    onChange?: (date: Dayjs | null) => void;
    format?: string;
    picker?: 'date' | 'week' | 'month' | 'quarter' | 'year';
    allowClear?: boolean;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
    disabledDate?: (currentDate: Dayjs) => boolean;
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;

    // Navigation props (optional - for controlled navigation)
    showNavigation?: boolean;
    onNavigateLeft?: () => void;
    onNavigateRight?: () => void;
    isNextDisabled?: boolean;
    showCalendarIcon?: boolean;

    /** data-testid for the plain (no-navigation) picker input; the navigation variant always uses period-picker */
    testId?: string;
}

export const NSPDatePicker = ({
    value,
    onChange,
    format = 'YYYY-MM-DD',
    picker = 'date',
    allowClear = false,
    disabled = false,
    placeholder,
    className,
    style,
    showNavigation = false,
    onNavigateLeft,
    onNavigateRight,
    isNextDisabled = false,
    showCalendarIcon = false,
    disabledDate,
    getPopupContainer,
    testId,
}: NSPDatePickerProps) => {
    if (!showNavigation) {
        return (
            <DatePicker
                value={value}
                onChange={onChange}
                format={format}
                picker={picker}
                allowClear={allowClear}
                disabled={disabled}
                placeholder={placeholder}
                className={className || styles.nspDatePicker}
                style={style}
                disabledDate={disabledDate}
                getPopupContainer={getPopupContainer}
                {...(testId && { 'data-testid': testId })}
            />
        );
    }

    return (
        <div className={`nsp-datepicker-container ${className || ''}`} style={style} data-testid="period-picker">
            {showCalendarIcon && <CalendarOutlined className="nsp-datepicker-calendar-icon" />}

            <Button
                type="text"
                icon={<LeftOutlined />}
                className="nsp-datepicker-nav-btn"
                onClick={onNavigateLeft}
                disabled={disabled}
                data-testid="period-prev-button"
            />

            <DatePicker
                picker={picker}
                allowClear={allowClear}
                value={value}
                format={format}
                onChange={onChange}
                disabled={disabled}
                placeholder={placeholder}
                disabledDate={disabledDate}
            />

            <Button
                type="text"
                icon={<RightOutlined />}
                className={`nsp-datepicker-nav-btn ${isNextDisabled ? 'nsp-datepicker-disabled-btn' : ''}`}
                onClick={onNavigateRight}
                disabled={isNextDisabled || disabled}
                data-testid="period-next-button"
            />
        </div>
    );
};

export default NSPDatePicker;